import { createStore } from 'zustand/vanilla'
import type {
  ConnectionQualityLevel,
  ConnectionStatus,
  DisconnectReasonCode,
  LocalMediaFailure,
  ParticipantSnapshot,
  TrackSnapshot,
} from './adapter'

// Медиа-состояние комнаты живёт вне React. Адаптер (real/fake) — единственный
// писатель: маппит события SDK в эти редьюсеры. React-обвязка
// (useSyncExternalStore) — в entities/call, не здесь.

export interface LocalMediaState {
  mic: boolean
  cam: boolean
  screen: boolean
}

export interface CallStoreState {
  status: ConnectionStatus
  disconnectReason?: DisconnectReasonCode | undefined
  participants: ParticipantSnapshot[]
  activeSpeakerIds: string[]
  // «Липкий» активный спикер для крупного плана: говорящий занимает план и
  // держит его; в тишине план остаётся за последним спикером; передача
  // заговорившему другому — через грейс (короткие реплики не дёргают
  // раскладку), отменяется, если текущий возобновил речь.
  spotlightId: string | null
  // MRU-история носителей spotlight: последний спикер — первым, предыдущие
  // сдвигаются следом. Драйвер порядка ленты миниатюр (в т.ч. при демонстрации
  // экрана); наследует грейс spotlight — короткие реплики порядок не дёргают.
  speakerRank: string[]
  localMedia: LocalMediaState
  // Отказ локального устройства: вход состоялся, но микрофон/камера не поднялись.
  // Молчаливых отказов нет — комната показывает это тостом.
  localMediaFailure: LocalMediaFailure | null
  // Браузер не дал воспроизвести звук: участники говорят, а человек их не
  // слышит и не понимает почему. Комната показывает кнопку «Включить звук».
  audioBlocked: boolean
}

export interface CallStoreActions {
  reset: () => void
  setStatus: (status: ConnectionStatus, reason?: DisconnectReasonCode) => void
  upsertParticipant: (participant: ParticipantSnapshot) => void
  removeParticipant: (id: string) => void
  setParticipantTracks: (id: string, tracks: TrackSnapshot[]) => void
  setConnectionQuality: (id: string, quality: ConnectionQualityLevel) => void
  setActiveSpeakers: (ids: string[]) => void
  setLocalMedia: (patch: Partial<LocalMediaState>) => void
  setLocalMediaFailure: (failure: LocalMediaFailure | null) => void
  setAudioBlocked: (blocked: boolean) => void
}

export type CallStore = CallStoreState & { actions: CallStoreActions }

const emptyState: CallStoreState = {
  status: 'disconnected',
  disconnectReason: undefined,
  participants: [],
  activeSpeakerIds: [],
  spotlightId: null,
  speakerRank: [],
  localMedia: { mic: false, cam: false, screen: false },
  localMediaFailure: null,
  audioBlocked: false,
}

function replaceAt<T>(list: T[], index: number, value: T): T[] {
  const next = list.slice()
  next[index] = value
  return next
}

function promote(rank: string[], id: string | null): string[] {
  if (id == null) return rank
  if (rank[0] === id) return rank
  return [id, ...rank.filter((x) => x !== id)]
}

// Грейс передачи крупного плана другому спикеру.
export const SPOTLIGHT_HANDOVER_MS = 500

let handoverTimer: ReturnType<typeof setTimeout> | null = null
function cancelHandover() {
  if (handoverTimer != null) {
    clearTimeout(handoverTimer)
    handoverTimer = null
  }
}

export const callStore = createStore<CallStore>()((set) => ({
  ...emptyState,
  actions: {
    reset: () => {
      cancelHandover()
      set({ ...emptyState })
    },
    setStatus: (status, reason) => set({ status, disconnectReason: reason }),
    upsertParticipant: (participant) =>
      set((s) => {
        const index = s.participants.findIndex((p) => p.id === participant.id)
        return index === -1
          ? { participants: [...s.participants, participant] }
          : { participants: replaceAt(s.participants, index, participant) }
      }),
    removeParticipant: (id) =>
      set((s) => {
        const activeSpeakerIds = s.activeSpeakerIds.filter((x) => x !== id)
        // Спикер в крупном плане вышел — план сразу у говорящего (или пуст).
        const spotlightId = s.spotlightId === id ? (activeSpeakerIds[0] ?? null) : s.spotlightId
        return {
          participants: s.participants.filter((p) => p.id !== id),
          activeSpeakerIds,
          spotlightId,
          speakerRank: promote(
            s.speakerRank.filter((x) => x !== id),
            spotlightId,
          ),
        }
      }),
    setParticipantTracks: (id, tracks) =>
      set((s) => {
        const index = s.participants.findIndex((p) => p.id === id)
        if (index === -1) return {}
        const current = s.participants[index]
        if (!current) return {}
        return { participants: replaceAt(s.participants, index, { ...current, tracks }) }
      }),
    setConnectionQuality: (id, quality) =>
      set((s) => {
        const index = s.participants.findIndex((p) => p.id === id)
        if (index === -1) return {}
        const current = s.participants[index]
        if (!current) return {}
        return {
          participants: replaceAt(s.participants, index, {
            ...current,
            connectionQuality: quality,
          }),
        }
      }),
    setActiveSpeakers: (ids) =>
      set((s) => {
        const next: Partial<CallStoreState> = {
          activeSpeakerIds: ids,
          participants: s.participants.map((p) => ({ ...p, speaking: ids.includes(p.id) })),
        }
        if (s.spotlightId != null && ids.includes(s.spotlightId)) {
          // Текущий спикер говорит (в т.ч. возобновил речь) — держим,
          // запланированная передача отменяется.
          cancelHandover()
        } else if (ids.length === 0) {
          // Тишина — план липнет к последнему спикеру, передавать некому.
          cancelHandover()
        } else if (s.spotlightId == null) {
          cancelHandover()
          next.spotlightId = ids[0] ?? null
          next.speakerRank = promote(s.speakerRank, next.spotlightId)
        } else if (handoverTimer == null) {
          // Говорит другой — передача после грейса; наследника выбираем в момент
          // срабатывания по свежему состоянию, а не по снимку из замыкания.
          handoverTimer = setTimeout(() => {
            handoverTimer = null
            const st = callStore.getState()
            const heir = st.activeSpeakerIds.find((x) => st.participants.some((p) => p.id === x))
            if (heir != null && heir !== st.spotlightId)
              callStore.setState({ spotlightId: heir, speakerRank: promote(st.speakerRank, heir) })
          }, SPOTLIGHT_HANDOVER_MS)
        }
        return next
      }),
    setLocalMedia: (patch) => set((s) => ({ localMedia: { ...s.localMedia, ...patch } })),
    setLocalMediaFailure: (failure) => set({ localMediaFailure: failure }),
    setAudioBlocked: (audioBlocked) => set({ audioBlocked }),
  },
}))

export type CallStoreApi = typeof callStore
